type CSSPropertyName = keyof Omit<
  CSSStyleDeclaration,
  'length' | 'parentRule' | 'getPropertyPriority' | 'getPropertyValue' | 'item' | 'removeProperty' | 'setProperty'
> &
  string;

type ContextSize = {
  width?: string | number;
  height?: string | number;
};

export type CssValueOptions = {
  /** The CSS property to measure (e.g., 'width', 'fontSize', 'margin') */
  property: CSSPropertyName;
  /** Optional element to inherit context from (default: document.documentElement) */
  element?: HTMLElement;
  /** Optional context size for relative units (%, em, etc.) */
  context?: ContextSize;
};

/**
 * Converts any CSS size value to its computed value.
 *
 * This function can handle various CSS values including:
 * - Absolute units (px, pt, cm, mm, in)
 * - Relative units (rem, em, %, vh, vw)
 * - calc() expressions
 * - CSS variables
 * - Combinations of the above
 *
 * @param cssValue - The CSS value to convert (e.g., "rgb(255, 215, 200)", "#123123", "var(--mantine-white)", "1.5rem", "calc(100% - 20px)", "calc(2.5rem * var(--scale))")
 * @param options - Configuration options
 *
 * @example
 * // Convert font size
 * const fontSize = convertCssValueToResolvedValue('calc(2.5rem * var(--scale))', { property: 'fontSize' });
 *
 * // Convert width with specific context
 * const width = convertCssValueToResolvedValue('50%', {
 *   property: 'width',
 *   context: { width: '200px' }
 * });
 *
 * // Convert margin with specific element context
 * const margin = convertCssValueToResolvedValue('2em', {
 *   property: 'margin',
 *   element: document.getElementById('myElement')
 * });
 *
 * @returns The computed value as is
 */
export function convertCssValueToResolvedValue(cssValue: string | number, options: CssValueOptions): string {
  const { property = 'width', element = document.documentElement, context } = options;

  // If value is already a number, assume it's in pixels
  if (typeof cssValue === 'number') {
    return `${cssValue}px`;
  }

  // Create a temporary element to compute the value
  const temp = document.createElement('div');
  temp.style.visibility = 'hidden';
  temp.style.position = 'absolute';

  // Set the property we want to measure
  temp.style[property] = cssValue;

  // Set context size if provided, otherwise use default for layout properties
  if (context) {
    if (context.width) {
      temp.style.width = typeof context.width === 'number' ? `${context.width}px` : context.width;
    }
    if (context.height) {
      temp.style.height = typeof context.height === 'number' ? `${context.height}px` : context.height;
    }
  } else {
    // Properties that typically need layout context
    const needsLayout = [
      'width',
      'height',
      'top',
      'right',
      'bottom',
      'left',
      'margin',
      'padding',
      'fontSize',
      'lineHeight'
    ];

    if (needsLayout.some(prop => property.includes(prop))) {
      temp.style.width = '100px';
      temp.style.height = '100px';
    }
  }

  // Append to the element to inherit CSS variables and context
  element.appendChild(temp);

  // Get the computed value in pixels
  const computedValue = window.getComputedStyle(temp)[property];
  const resolvedValue = computedValue;

  // Clean up
  element.removeChild(temp);

  return resolvedValue;
}
